import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { dateUtil } from "src/app/support/date-util";
import { compact, find, first, flatten, pickBy } from "lodash";
import { Observable, defer, forkJoin, iif, map, of, switchMap } from "rxjs";
import {
  MAX_SHIFT_END_AT,
  Shift,
  ShiftCreate,
  ShiftDay,
  ShiftDayValue,
  ShiftLight,
  ShiftModel,
  ShiftPeriod,
  ShiftPeriodType,
  ShiftUpdate,
  isShiftRecurring,
  shiftDayToHHmmFormat
} from "src/app/backend/models/shift";
import { UserNotify } from "src/app/support/notify-decorator";
import { sntTranslate } from "src/app/support/translate";
import { environment } from "src/environments/environment";
import dayjs from "dayjs";
import { flat } from "radash";
import { appState } from "src/app/core/state";
import * as Sentry from "@sentry/angular";
import { getCustomerNominative } from "src/app/support/customer";

@Injectable()
export class ShiftBackend {
  private readonly http = inject(HttpClient);

  create(memberId: string, payload: ShiftCreate): Observable<Shift> {
    if (appState.tenantId === "682dc4d4468b611a4c8544c9") {
      Sentry.logger.debug("ShiftBackend::create", {
        currentMemberId: appState.member?.id,
        currentMemberName: getCustomerNominative(appState.member),
        memberId,
        startAt: payload.startAt,
        endAt: payload.endAt,
        days: JSON.stringify(payload.days),
        frequency: payload.frequency
      });
    }

    return this.http
      .post<Shift>(
        `${environment.snoots.api.calendar}/members/${memberId}/shifts`,
        payload
      )
      .pipe(
        map((shift: Shift) => {
          return extend(shift, shift.startAt);
        })
      );
  }

  listByDateAndMember({
    date,
    memberId
  }: {
    date: string;
    memberId: string;
  }): Observable<Shift | undefined> {
    return this.http
      .get<ShiftModel[]>(
        `${environment.snoots.api.calendar}/members/${memberId}/shifts`,
        {
          params: {
            date
          }
        }
      )
      .pipe(
        map((shiftsModels: ShiftModel[]) =>
          shiftsModels.map(
            (shiftModel: ShiftModel) =>
              <Shift>{ ...shiftModel, staff: { id: memberId } }
          )
        ),
        map((shifts: Shift[]) =>
          shifts.map((shift: Shift) => {
            return extend(shift, date);
          })
        ),
        map((shifts: Shift[]) => {
          if (shifts.length === 0) {
            return undefined;
          }

          if (shifts.length === 1) {
            return first(shifts)!;
          }

          shifts.reduce((previous: Shift, current: Shift) => {
            previous.parent = current;
            return current;
          }, first(shifts)!);

          return first(shifts);
        })
      );
  }

  listByDateAndMemberLight({
    date,
    memberId
  }: {
    date: string;
    memberId: string;
  }): Observable<ShiftLight | undefined> {
    return this.http
      .get<ShiftModel[]>(
        `${environment.snoots.api.calendar}/members/${memberId}/shifts`,
        {
          params: {
            light: true,
            date
          }
        }
      )
      .pipe(
        map((shiftsModels: ShiftModel[]) =>
          shiftsModels.map(
            (shiftModel: ShiftModel) =>
              <Shift>{ ...shiftModel, staff: { id: memberId } }
          )
        ),
        map((shifts: Shift[]) =>
          shifts.map((shift: Shift) => {
            return extend(shift, date);
          })
        ),
        map((shifts: Shift[]) => {
          if (shifts.length === 0) {
            return undefined;
          }

          if (shifts.length === 1) {
            return first(shifts)!;
          }

          shifts.reduce((previous: Shift, current: Shift) => {
            previous.parent = current;
            return current;
          }, first(shifts)!);

          return first(shifts);
        })
      );
  }

  listByDateAndMembers({
    date,
    memberIds
  }: {
    date: string;
    memberIds: string[];
  }): Observable<Shift[]> {
    if (!memberIds || memberIds.length === 0) {
      return of([]);
    }

    return forkJoin(
      memberIds.map((memberId: string) => {
        return this.listByDateAndMember({
          date,
          memberId
        });
      })
    ).pipe(
      map((shiftsOfMembers: Array<Shift | undefined>) => {
        return compact(flatten(shiftsOfMembers));
      })
    );
  }

  listByDateAndMembersLight({
    date,
    memberIds,
    tenant
  }: {
    date: string;
    memberIds: string[];
    tenant?: string;
  }): Observable<ShiftLight[]> {
    if (!memberIds || memberIds.length === 0) {
      return of([]);
    }

    return this.http
      .get<Array<{ memberId: string; shifts: ShiftLight[] }>>(
        `${environment.snoots.api.calendar}/shifts`,
        {
          params: pickBy({
            light: true,
            date,
            members: memberIds.join(","),
            tenant
          })
        }
      )
      .pipe(
        map((response) => {
          return flat(
            response.map((item: { memberId: string; shifts: ShiftLight[] }) => {
              return item.shifts.map((shift: ShiftLight) => {
                return {
                  ...shift,
                  staff: {
                    id: item.memberId
                  }
                };
              });
            })
          );
        }),
        map((shifts: ShiftLight[]) =>
          shifts.map((shift: Shift) => {
            return extend(shift, date);
          })
        )
      );
  }

  deleteForMemberByDate({
    date,
    memberId
  }: {
    date: string;
    memberId: string;
  }): Observable<void> {
    if (appState.tenantId === "682dc4d4468b611a4c8544c9") {
      Sentry.logger.debug("ShiftBackend::deleteForMemberByDate", {
        currentMemberId: appState.member?.id,
        currentMemberName: getCustomerNominative(appState.member),
        memberId,
        date
      });
    }

    return this.listByDateAndMember({ date, memberId }).pipe(
      switchMap((shift: Shift | undefined) => {
        return iif(
          () => !!shift,
          defer(() => {
            return this.delete({ id: shift!.id, memberId: memberId });
          }),
          of(void 0)
        );
      })
    );
  }

  @UserNotify({
    successMessage: sntTranslate(marker("Turni eliminati")),
    errorMessage: sntTranslate(marker("Impossibile eliminare i turni"))
  })
  deleteForMemberByDay(
    date: string,
    memberId: string,
    day: ShiftDayValue
  ): Observable<void> {
    if (appState.tenantId === "682dc4d4468b611a4c8544c9") {
      Sentry.logger.debug("ShiftBackend::deleteForMemberByDay", {
        currentMemberId: appState.member?.id,
        currentMemberName: getCustomerNominative(appState.member),
        memberId,
        day
      });
    }

    return this.listByDateAndMember({ date, memberId }).pipe(
      switchMap((shift: Shift | undefined) => {
        return iif(
          () => !!shift,
          defer(() => {
            return this.update({
              id: shift!.id,
              memberId: shift!.staff.id,
              payload: {
                days: shift!.days
                  .filter((shiftDay: ShiftDay) => shiftDay.day !== day)
                  .map((shiftDay: ShiftDay) => {
                    return shiftDayToHHmmFormat(shiftDay.day, shiftDay.periods);
                  })
              }
            });
          }),
          of(void 0)
        );
      })
    );
  }

  @UserNotify({
    successMessage: sntTranslate(marker("Configurazione eliminata")),
    errorMessage: sntTranslate(
      marker("Impossibile eliminare la configurazione")
    )
  })
  delete({ id, memberId }: { id: string; memberId: string }): Observable<void> {
    if (appState.tenantId === "682dc4d4468b611a4c8544c9") {
      Sentry.logger.debug("ShiftBackend::delete", {
        currentMemberId: appState.member?.id,
        currentMemberName: getCustomerNominative(appState.member),
        shiftId: id,
        memberId
      });
    }

    return this.http.delete<void>(
      `${environment.snoots.api.calendar}/members/${memberId}/shifts/${id}`
    );
  }

  @UserNotify({
    successMessage: sntTranslate(marker("Configurazione aggiornata")),
    errorMessage: (errorResponse: HttpErrorResponse) => {
      const { statusCode } = errorResponse.error;

      if (statusCode === 409) {
        return sntTranslate(
          marker("Conflitto tra due turni uguali o parzialmente sovrapposti")
        );
      }

      return sntTranslate(marker("Impossibile aggiornare la configurazione"));
    }
  })
  update({
    id,
    memberId,
    payload
  }: {
    id: string;
    memberId: string;
    payload: ShiftUpdate;
  }): Observable<void> {
    if (appState.tenantId === "682dc4d4468b611a4c8544c9") {
      Sentry.logger.debug("ShiftBackend::update", {
        currentMemberId: appState.member?.id,
        currentMemberName: getCustomerNominative(appState.member),
        shiftId: id,
        memberId,
        endAt: payload.endAt,
        days: JSON.stringify(payload.days),
        frequency: payload.frequency
      });
    }

    return this.http.patch<void>(
      `${environment.snoots.api.calendar}/members/${memberId}/shifts/${id}`,
      payload
    );
  }
}

function extend(shift: Shift, date: string): Shift {
  return {
    ...shift,
    recurring: isShiftRecurring(shift, date),
    maxEnd: dayjs(shift.endAt).isSame(dayjs(MAX_SHIFT_END_AT), "year"),
    days:
      shift.days?.map((shiftDay: ShiftDay) => {
        return {
          ...shiftDay,
          rest: !!find(
            shiftDay.periods || [],
            (period: ShiftPeriod) => period.type === ShiftPeriodType.REST
          ),
          vacation: !!find(
            shiftDay.periods || [],
            (period: ShiftPeriod) => period.type === ShiftPeriodType.VACATION
          ),
          periods:
            shiftDay.periods?.map((shiftPeriod: ShiftPeriod) => {
              const [hoursStartAt, minutesStartAt] =
                shiftPeriod.startAt.split(":");
              const [hoursEndAt, minutesEndAt] = shiftPeriod.endAt.split(":");

              try {
                return {
                  ...shiftPeriod,
                  startAt: dateUtil(date).isValid()
                    ? dateUtil(date)
                        .set("hours", parseInt(hoursStartAt))
                        .set("minutes", parseInt(minutesStartAt))
                        .toISOString()
                    : "",
                  endAt: dateUtil(date).isValid()
                    ? dateUtil(date)
                        .set("hours", parseInt(hoursEndAt))
                        .set("minutes", parseInt(minutesEndAt))
                        .toISOString()
                    : ""
                };
              } catch (ex) {
                return shiftPeriod;
              }
            }) || []
        };
      }) || []
  };
}
